import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import {
  FALLBACK_LANG,
  isLang,
  SUPPORTED_LANGS,
  type AppConfigResponse,
  type Lang,
} from "@shared";
import en from "./locales/en.json";
import fr from "./locales/fr.json";

const STORAGE_KEY = "cet.lang";

/**
 * Langue au chargement : choix precedent du jeune, sinon la langue par
 * defaut du site.
 *
 * La langue du NAVIGATEUR n'est volontairement PAS consultee. Le jeu s'adresse
 * a des jeunes francophones, qui jouent souvent sur le telephone d'un parent
 * — souvent configure en anglais. Se fier au navigateur affichait alors un jeu
 * anglais a un jeune francais, sans qu'il sache pourquoi ni comment en sortir.
 * L'anglais reste accessible par la bascule de langue, qui est memorisee.
 */
function storedLang(): Lang | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLang(stored) ? stored : null;
  } catch {
    // Stockage indisponible (navigation privee) : pas de preference retenue.
    return null;
  }
}

/** Change la langue et la retient pour les prochaines visites. */
export async function setLang(lang: Lang): Promise<void> {
  await i18next.changeLanguage(lang);
  document.documentElement.lang = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Sans stockage, la langue vaut pour la session en cours seulement.
  }
}

export function currentLang(): Lang {
  return isLang(i18next.resolvedLanguage) ? i18next.resolvedLanguage : FALLBACK_LANG;
}

const preference = storedLang();
const initial = preference ?? FALLBACK_LANG;
document.documentElement.lang = initial;

// Les ressources sont fournies en dur : l'init est synchrone, pas besoin
// d'attendre la promesse avant le premier rendu.
void i18next.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
  },
  lng: initial,
  fallbackLng: FALLBACK_LANG,
  supportedLngs: [...SUPPORTED_LANGS],
  interpolation: { escapeValue: false }, // React echappe deja
});

/**
 * Applique DEFAULT_LANG du serveur, seulement si le jeune n'a pas deja choisi.
 *
 * Fait APRES l'initialisation, sans bloquer le premier rendu : la page
 * s'affiche immediatement en francais, et ne bascule que si le serveur demande
 * autre chose. Hors-ligne, l'appel echoue et le francais reste — exactement le
 * comportement voulu.
 */
if (preference === null) {
  void fetch("/api/config")
    .then((response) => (response.ok ? (response.json() as Promise<AppConfigResponse>) : null))
    .then((config) => {
      if (config !== null && isLang(config.defaultLang) && config.defaultLang !== initial) {
        void i18next.changeLanguage(config.defaultLang);
        document.documentElement.lang = config.defaultLang;
      }
    })
    .catch(() => {
      // Serveur injoignable : on reste sur la langue de repli.
    });
}

export default i18next;
